import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { markFirstLoginSeen } from '../utils/firstLoginDetect';
import type { BannerSpec } from './banners/types';

/**
 * Shown once — on the first authenticated session for a free user, in the
 * top-slot of the Home dashboard.
 *
 * Copy rationale: the user just signed in, which means they already decided
 * Pastewaves is worth keeping. This card confirms that decision ("your files
 * are safe now") and makes the upgrade ask feel like a natural next step, not
 * a sales pitch.
 *
 * Marks itself seen on mount via markFirstLoginSeen() so back-navigation
 * and page refreshes do not re-show it.
 */
const FirstLoginUpgradeHero: React.FC = () => {
  const { getUserId } = useAuth();
  const navigate = useNavigate();
  const userId = getUserId() ?? '';
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Mark seen immediately — the card is rendering, intent is registered.
    markFirstLoginSeen(userId);
  }, [userId]);

  if (dismissed) return null;

  return (
    <motion.div
      className="w-full rounded-2xl border p-6 md:p-8 relative overflow-hidden mb-8"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 72, 0, 0.12) 0%, rgba(6, 182, 212, 0.08) 100%)',
        borderColor: 'rgba(255, 72, 0, 0.25)'
      }}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      {/* Subtle background glow — pointer-events-none so it can't intercept clicks */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-soundwave-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        {/* Left — message. Left-aligned on all breakpoints: this is personal copy,
            not a marketing tagline. Centering would make it feel generic. */}
        <div className="flex-1 text-left">
          <h3
            className="text-2xl md:text-3xl font-extrabold leading-tight mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            Your files are safe now.
          </h3>
          <p className="text-sm md:text-base" style={{ color: 'var(--text-secondary)' }}>
            You signed in — they're yours for 14 days. Go Pro and they stay forever.
          </p>
        </div>

        {/* Right — CTA. Full-width on mobile so the button fills the space;
            auto-width on md+ where the flex-row layout provides natural sizing. */}
        <div className="flex flex-col items-stretch md:items-center gap-3 w-full md:w-auto flex-shrink-0">
          <motion.button
            onClick={() => navigate('/pro')}
            className="flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-soundwave-500 to-soundwave-600 hover:from-soundwave-600 hover:to-soundwave-700 text-white font-bold rounded-xl shadow-lg transition-all"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            Keep them forever — $5/mo
            <ArrowRight className="w-4 h-4 flex-shrink-0" />
          </motion.button>

          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-sm text-center hover:underline transition-colors bg-transparent border-0 p-0 cursor-pointer"
            style={{ color: 'var(--text-tertiary)' }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default FirstLoginUpgradeHero;

/**
 * Welcome-tier spec: fires on the first authenticated session for a free user
 * on this browser. The card writes its own seen-flag on mount via
 * `markFirstLoginSeen`, so we MUST NOT read the live `isFirstLogin(userId)`
 * here — that would race the mount-write: the card renders, its
 * `useEffect` writes the flag, the very next render (e.g. when Home's
 * `userFiles` refetch settles and re-runs `HomeTopSlot`) re-evaluates this
 * predicate, sees the flag, returns false, and the card collapses after a
 * single frame.
 *
 * The slot reads `useIsFirstLoginSession()` once per userId and freezes the
 * value into `BannerInputs.isFirstLoginSession` for the lifetime of the
 * React app's session. This predicate consumes that frozen value, so the
 * card stays mounted for the duration of the page view; only a refresh (new
 * React mount) clears the welcome state — at which point the underlying
 * `isFirstLogin` has already flipped to false, the hook captures false, and
 * the slot collapses to the next-tier card.
 *
 * `isFirstLogin` and `markFirstLoginSeen` remain the source of truth in
 * `firstLoginDetect.ts`; only the spec consumes the session-stable hook.
 */
export const firstLoginUpgradeHeroSpec: BannerSpec = {
  id: 'first-login-upgrade',
  category: 'welcome',
  when: ({ isFirstLoginSession }) => isFirstLoginSession,
  render: () => <FirstLoginUpgradeHero />,
};
